/**
 * Shared admin UI pieces.
 *
 * Hand-built rather than pulled from a component kit — the newest projects in
 * this codebase deliberately dropped Radix, and this app needs perhaps a dozen
 * primitives, all of them small.
 */

import { AlertCircle, CheckCircle2, Loader2, Info } from 'lucide-react';
import type { RequestStatus } from '@ach/shared';
import { REQUEST_STATUS_LABELS } from '@ach/shared';

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      {label}…
    </div>
  );
}

export function ErrorNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div>{children}</div>
    </div>
  );
}

export function WarningNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div>{children}</div>
    </div>
  );
}

export function InfoNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
      <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div>{children}</div>
    </div>
  );
}

export function SuccessNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div>{children}</div>
    </div>
  );
}

export function Field({
  label,
  error,
  hint,
  children,
  required,
}: {
  label: string;
  error?: string;
  hint?: string;
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <div>
      <label className="field-label">
        {label}
        {required && <span className="ml-0.5 text-red-600">*</span>}
      </label>
      {children}
      {hint && !error && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
      {error && <p className="field-error">{error}</p>}
    </div>
  );
}

const STATUS_STYLES: Record<RequestStatus, string> = {
  draft: 'bg-slate-100 text-slate-700',
  sent: 'bg-blue-50 text-blue-800',
  viewed: 'bg-indigo-50 text-indigo-800',
  submitted: 'bg-amber-50 text-amber-900',
  recorded: 'bg-emerald-50 text-emerald-800',
  cancelled: 'bg-slate-100 text-slate-500',
  expired: 'bg-slate-100 text-slate-500',
};

export function StatusBadge({ status }: { status: RequestStatus }) {
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[status]}`}
    >
      {REQUEST_STATUS_LABELS[status]}
    </span>
  );
}

/**
 * Where an invoice amount came from.
 *
 * Worth its own badge rather than a footnote: this is the difference between a
 * number the PDF vouched for and one somebody typed at 4pm, and it is the
 * first thing to check if a debit is ever disputed.
 */
export function ParseBadge({ status }: { status: 'parsed' | 'manual' | 'failed' }) {
  if (status === 'parsed') {
    return (
      <span
        className="inline-flex rounded bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-800"
        title="Read from the PDF, and the invoice's own columns added up."
      >
        from PDF
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span
        className="inline-flex rounded bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-900"
        title="The PDF was read but its numbers did not reconcile, so this was entered by hand."
      >
        entered by hand
      </span>
    );
  }
  return (
    <span
      className="inline-flex rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-600"
      title="Entered by hand."
    >
      entered by hand
    </span>
  );
}

export function EmptyState({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center">
      <p className="font-medium text-slate-700">{title}</p>
      {children && <div className="mt-1 text-sm text-slate-500">{children}</div>}
    </div>
  );
}
